import { Box, Container, Flex, Grid, Heading, SystemStyleObject, Text } from '@chakra-ui/react';
import {
  AppLink,
  AppLinkWithArrow,
  Card,
  DiscordIcon,
  fullSizeParentStyles,
  GitbookIcon,
  InstagramIcon,
  KongActionIcon,
  LANDING_ROUTES,
  pxToRem,
  SHARED_ROUTES,
  TRANSITIONS,
  TwitterIconWithLink,
  VideoAutoplay,
} from '@cyberkongz/ui';
import { SliderWrapper } from '@cyberkongz/ui-slider';
import { PropsWithChildren, useEffect, useState } from 'react';
import { Mousewheel } from 'swiper/modules';
import { SwiperSlide, useSwiper } from 'swiper/react';

const textStyles: SystemStyleObject = {
  color: 'textSecondary',
  maxWidth: '28.1em',
};

const headingStyles: SystemStyleObject = {
  mt: [pxToRem(193), 0],
};

function SlideWrapper({
  slideBg,
  children,
  videoStyles,
  bgStyles,
}: {
  slideBg: string;
  videoStyles?: SystemStyleObject;
  bgStyles?: SystemStyleObject;
} & PropsWithChildren) {
  const isVideo = slideBg.includes('.mp4') || slideBg.includes('.webm');

  return (
    <Box
      css={{
        bg: isVideo
          ? 'undefined'
          : `url(/static/blueprint/vision/${slideBg}) center/cover no-repeat`,
        height: 'inherit',
        width: '100%',
        ...bgStyles,
      }}
    >
      {isVideo ? (
        <VideoAutoplay
          src={`/static/blueprint/vision/${slideBg}`}
          fallbackVideoExtension="mp4"
          posterExtension="jpg"
          css={{
            ...fullSizeParentStyles,
            objectFit: 'cover',
            zIndex: 1,
            ...videoStyles,
          }}
        />
      ) : null}
      <Container
        css={{
          pt: pxToRem(265),
          pb: [10, pxToRem(100)],
          position: 'relative',
          zIndex: 2,
        }}
      >
        {children}
      </Container>
    </Box>
  );
}

function DriveSlide() {
  return (
    <SlideWrapper slideBg="drive_slide_bg.jpg">
      <Flex direction={['column', 'row']}>
        <Box>
          <Heading mb={4}>
            what drives{' '}
            <Heading
              as="span"
              variant="displayPrimary"
              css={{
                position: 'relative',
                top: '0.07em',
                ml: [3, 4],
              }}
            >
              us
            </Heading>
          </Heading>
          <Box css={textStyles}>
            <Text fontWeight="medium" mb={5}>
              Our vision is clear. Our focus, unwavering.
            </Text>
            <Text>
              We are the Kongz of the Web3 Jungle. We came, we saw, we build. We look to constantly
              and purposefully innovate. We do not follow the road already taken. We are pioneers.
              To the best of our ability we will blaze new trails. We are unconventional and unique.
              So too is our path.
            </Text>
          </Box>
        </Box>
        <Flex
          css={{
            position: 'relative',
            bg: 'radial-gradient(44.91% 44.91% at 50% 50%, rgba(0, 227, 97, 0.1) 0%, rgba(0, 227, 97, 0.25) 0.01%, rgba(0, 227, 97, 0) 100%)',
            order: [-1, 1],
            mb: [4, 0],
            flex: 1,
            justifyContent: ['center', 'flex-end'],
          }}
        >
          <VideoAutoplay
            src="/static/blueprint/vision/drive_astronaut.webm"
            posterExtension="png"
            transform="rotateZ(20deg)"
            css={{
              width: [pxToRem(193), 'auto'],
            }}
          />
          <VideoAutoplay
            src="/static/blueprint/vision/drive_fuel_rod.webm"
            posterExtension="png"
            css={{
              position: 'absolute',
              transform: 'rotateZ(-20deg)',
              left: [pxToRem(20), '20%'],
              top: ['25%', '42.5%'],
              width: [pxToRem(113), '30%'],
            }}
          />
        </Flex>
      </Flex>
    </SlideWrapper>
  );
}

const INNOVATE_ITEMS = [
  { label: 'The Guardian Contract', href: LANDING_ROUTES.GUARDIAN_CONTRACT_ARTICLE },
  {
    label: 'Progressive VX asset files',
    href: LANDING_ROUTES.BLOG_POST('updated-fbx-files-in-the-jungle'),
  },
  {
    label: 'NFT Staking 2.0 - Use, not lose your assets.',
    href: SHARED_ROUTES.DOCS_PATH('/what-happens-on-the-blockchain/lock-registry'),
  },
  { label: 'Fully verifiable onchain jungle adventures', href: LANDING_ROUTES.PLAY_AND_KOLLECT },
];

function InnovateSlide() {
  return (
    <SlideWrapper
      slideBg="innovate_slide_bg.webm"
      videoStyles={{
        objectPosition: [`top 0 right ${pxToRem(-200)}`, 'top right'],
      }}
    >
      <Heading css={headingStyles}>Innovate, innovate,</Heading>
      <Heading variant="displayPrimary" mb={4}>
        innovate
      </Heading>
      <Text css={textStyles}>
        We are builders at heart. We build with purpose. We continue to build in order to make a
        visible impact in Web3. We will continue to push the boundaries of Web3 potential.
      </Text>
      <Card variant="landing" css={{ mt: 8, maxW: ['30em', null, pxToRem(812)] }}>
        <Heading size="2xs" css={{ mb: 4 }}>
          Recent innovations
        </Heading>
        <Grid
          css={{
            gap: 2,
            fontWeight: 'medium',
            gridTemplateColumns: ['repeat(1, 1fr)', null, 'repeat(2, 1fr)'],
          }}
        >
          {INNOVATE_ITEMS.map(({ label, href }) => (
            <AppLinkWithArrow key={label} href={href}>
              {label}
            </AppLinkWithArrow>
          ))}
        </Grid>
      </Card>
    </SlideWrapper>
  );
}

function InteroperabilitySlide() {
  return (
    <SlideWrapper
      slideBg="interoperability_slide_bg.webm"
      videoStyles={{
        objectPosition: [`top 0 right ${pxToRem(-350)}`, 'right bottom'],
      }}
    >
      <Box>
        <Heading variant="displayPrimary" mb={4} css={headingStyles}>
          Interoperability
        </Heading>
        <Text css={textStyles}>
          We dream to make Kongz interoperable in everything they will fit, and will provide all
          necessary assets for holders to build on top of Kongz.
        </Text>
        <Text css={textStyles} mt={[3, 6]}>
          Interested in integrating CyberKongz into your project? We can help provide the necessary
          files to make this possible. Contact us at{' '}
          <AppLink
            css={{ fontWeight: 'medium', textDecoration: 'underline' }}
            href="mailto:integrations@cyberkongz.com"
          >
            integrations@cyberkongz.com
          </AppLink>
        </Text>
      </Box>
    </SlideWrapper>
  );
}

const COMMUNITY_SLIDE_ICONS = [
  { icon: <DiscordIcon />, label: 'Discord' },
  { icon: <TwitterIconWithLink />, label: 'Twitter' },
  { icon: <InstagramIcon />, label: 'Instagram' },
  { icon: <GitbookIcon />, label: 'Gitbook' },
];

function CommunitySlide() {
  return (
    <SlideWrapper
      slideBg="community_slide_bg.jpg"
      bgStyles={{
        backgroundPositionX: [`${pxToRem(-700)}`, 'right'],
      }}
    >
      <Heading variant="displayPrimary" mb={4} css={headingStyles}>
        Community focused
      </Heading>
      <Text css={textStyles}>
        We build WITH and FOR the community. We host a dedicated community with some of the finest
        innovators, creators, and experts in Web3.
      </Text>
      <Box position="relative">
        <Card variant="landingTransparent" css={{ maxWidth: '28.1em', mt: [3, 6] }}>
          <Heading color="inherit" size="2xs" pb={pxToRem(50)}>
            BE PART OF THE COMMUNITY
          </Heading>
        </Card>
        <Flex
          css={{
            position: 'absolute',
            left: [4, 8],
            bottom: [-7, -3],
            transform: 'translateY(-100%)',
            alignItems: 'center',
            gap: pxToRem(6),
            '& svg': {
              maxH: 6,
              w: 'auto',
            },
          }}
        >
          {COMMUNITY_SLIDE_ICONS.map(({ icon, label }) => (
            <KongActionIcon key={label} label={label}>
              {icon}
            </KongActionIcon>
          ))}
        </Flex>
      </Box>
    </SlideWrapper>
  );
}

const SLIDES_COUNT = 4;

function SliderPaginationItem({ isActive, onClick }: { isActive?: boolean; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      css={{
        cursor: 'pointer',
        px: [0, 2],
        py: [2, 0],
      }}
    >
      <Box
        css={{
          w: [pxToRem(71), pxToRem(4)],
          h: [pxToRem(4), pxToRem(71)],
          bg: 'white',
          transition: TRANSITIONS.standard,
          opacity: isActive ? 1 : 0.3,
        }}
      />
    </Box>
  );
}

function SliderPagination() {
  const swiper = useSwiper();
  // workaround for swiper instance not updating on rerender
  const [swiperInternal, setSwiperInternal] = useState({
    activeIndex: 0,
  });

  useEffect(() => {
    swiper.on('slideChange', ({ activeIndex }) => {
      setSwiperInternal((prevState) => ({ ...prevState, activeIndex }));

      // if user is on last slide to position accordingly
      if (activeIndex === 2) {
        window.scrollTo(0, 0);
      }

      if (activeIndex === 3) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 250);
      }
    });
  }, [swiper]);

  return (
    <Box
      css={{
        position: 'absolute',
        zIndex: 1,
        right: [0, pxToRem(35)],
        left: [0, 'auto'],
        top: ['auto', 0],
        bottom: [pxToRem(10), 0],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: [4, 0],
      }}
    >
      <Grid css={{ gridTemplateColumns: ['repeat(4, 1fr)', 'repeat(1, 1fr)'], gap: 2 }}>
        {Array.from(Array(SLIDES_COUNT).keys()).map((_, i) => (
          <SliderPaginationItem
            key={i}
            isActive={swiperInternal.activeIndex === i}
            onClick={() => swiper.slideTo(i)}
          />
        ))}
      </Grid>
    </Box>
  );
}

export function VisionTab({ setIsNavHidden }: { setIsNavHidden: (isNavHidden: boolean) => void }) {
  return (
    <Box css={{ height: 'auto', minHeight: '100vh' }}>
      <SliderWrapper
        onSlideChange={({ isBeginning, isEnd }) => {
          if (isBeginning || isEnd) {
            setIsNavHidden(false);
          } else {
            setIsNavHidden(true);
          }
        }}
        modules={[Mousewheel]}
        css={{
          '& .swiper-wrapper': {
            height: ['auto', '100vh'],
            minHeight: ['100vh', pxToRem(700)],
            position: 'relative',
            display: 'flex',
          },
          '& .swiper-slide': {
            height: 'inherit',
            display: 'flex',
          },
        }}
        direction="horizontal"
        mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
        slidesPerView={1}
        breakpoints={{
          768: {
            direction: 'vertical',
          },
        }}
      >
        <SliderPagination />
        <SwiperSlide>
          <DriveSlide />
        </SwiperSlide>
        <SwiperSlide>
          <InnovateSlide />
        </SwiperSlide>
        <SwiperSlide>
          <InteroperabilitySlide />
        </SwiperSlide>
        <SwiperSlide>
          <CommunitySlide />
        </SwiperSlide>
      </SliderWrapper>
    </Box>
  );
}
